
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin } from 'lucide-react';

interface ServiceLocation {
  id: string;
  name: string;
  county: string;
  population: number;
  historical_fact: string | null;
  key_search_terms: string | null;
  latitude: number;
  longitude: number;
}

const ServiceAreaMap = () => {
  const [locations, setLocations] = useState<ServiceLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<ServiceLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const { data, error } = await supabase
          .from('service_locations')
          .select('*')
          .order('name');
        
        if (error) throw error;
        
        setLocations(data);
        if (data.length > 0) {
          // Default to showing Rocky Mount initially
          const rockyMount = data.find(loc => loc.name === 'Rocky Mount');
          setSelectedLocation(rockyMount || data[0]);
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError('Failed to load service locations');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  const filteredLocations = locations.filter(
    loc => loc.name.toLowerCase().includes(filter.toLowerCase())
  );

  const handleLocationSelect = (location: ServiceLocation) => {
    setSelectedLocation(location);
    // Scroll to location details on mobile
    const element = document.getElementById('location-details');
    if (element && window.innerWidth < 768) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderMap = () => {
    if (!selectedLocation) return null;
    
    return (
      <div className="w-full h-[400px] rounded-lg overflow-hidden">
        <iframe 
          title={`Map of ${selectedLocation.name}, NC`}
          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${selectedLocation.name},NC&zoom=13`}
          width="100%" 
          height="100%" 
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading service areas...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-8">{error}</div>;
  }

  return (
    <div className="container mx-auto py-8">
      <h2 className="text-3xl font-bold text-center mb-8">Our Service Areas</h2>
      
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search locations..."
          className="w-full p-2 border rounded-md"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      
      <div className="grid md:grid-cols-3 gap-6">
        {/* Location list - left column on desktop */}
        <div className="md:col-span-1 h-[500px] overflow-y-auto bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-2">
            {filteredLocations.map((location) => (
              <button
                key={location.id}
                onClick={() => handleLocationSelect(location)}
                className={`text-left p-2 rounded hover:bg-gray-200 transition ${
                  selectedLocation?.id === location.id ? 'bg-gray-200 font-medium' : ''
                }`}
              >
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-primary" />
                  <span>{location.name}</span>
                </div>
              </button>
            ))}
            {filteredLocations.length === 0 && (
              <p className="text-gray-500 p-2">No locations match your search.</p>
            )}
          </div>
        </div>
        
        {/* Map and details - right columns on desktop */}
        <div className="md:col-span-2" id="location-details">
          {renderMap()}
          
          {selectedLocation && (
            <Card className="mt-4">
              <CardContent className="pt-6">
                <div className="mb-4 flex justify-between items-center">
                  <h3 className="text-2xl font-bold">{selectedLocation.name}, NC</h3>
                  <Badge>{selectedLocation.county} County</Badge>
                </div>
                
                {selectedLocation.historical_fact && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-1">Historical Fact:</h4>
                    <p className="text-gray-700">{selectedLocation.historical_fact}</p>
                  </div>
                )}
                
                {selectedLocation.key_search_terms && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-1">Local Highlights:</h4>
                    <p className="text-gray-700">{selectedLocation.key_search_terms}</p>
                  </div>
                )}
                
                <div className="mb-4">
                  <h4 className="font-semibold mb-1">Population:</h4>
                  <p className="text-gray-700">{selectedLocation.population?.toLocaleString() || 'Not available'}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-1">Our Roofing Services in {selectedLocation.name}:</h4>
                  <ul className="list-disc pl-5 text-gray-700">
                    <li>Full roof replacements</li>
                    <li>Roof repairs</li>
                    <li>Storm damage assessment</li>
                    <li>Insurance claim assistance</li>
                    <li>Free estimates</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceAreaMap;
